import React, { useEffect, useMemo, useState } from 'react';

const toggleValue = (list, value) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

const removeValue = (list, value) => list.filter((item) => item !== value);

const EXPERIENCE_OPTIONS = [
  { value: 'Entry', label: 'Entry Level' },
  { value: 'Mid', label: 'Mid Level' },
  { value: 'Senior', label: 'Senior Level' },
];

const WORK_MODE_OPTIONS = [
  { value: 'Remote', label: 'Remote' },
  { value: 'Hybrid', label: 'Hybrid' },
  { value: 'On-Site', label: 'On-Site' },
];

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 11.5 12 5l8 6.5" />
    <path d="M6.5 10.5V19h11v-8.5" />
    <path d="M10 19v-5h4v5" />
  </svg>
);

const SearchPanel = ({
  searchText = '',
  onSearchTextChange,
  onSearch,
  onGoHome,
  companyOptions = [],
  cityOptions = [],
  standardTitleOptions = [],
  activeFilters = {},
}) => {
  const [activeMenu, setActiveMenu] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [standardTitleQuery, setStandardTitleQuery] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedStandardTitles, setSelectedStandardTitles] = useState([]);
  const [selectedWorkModes, setSelectedWorkModes] = useState([]);
  const [selectedExperienceLevels, setSelectedExperienceLevels] = useState([]);
  const selectedCount =
    selectedCompanies.length +
    selectedCities.length +
    selectedStandardTitles.length +
    selectedWorkModes.length +
    selectedExperienceLevels.length;

  useEffect(() => {
    setSelectedCompanies(activeFilters?.companies || []);
    setSelectedCities(activeFilters?.cities || []);
    setSelectedStandardTitles(activeFilters?.standardTitles || []);
    setSelectedWorkModes(activeFilters?.workModes || []);
    setSelectedExperienceLevels(activeFilters?.experienceLevels || []);
  }, [
    activeFilters?.companies,
    activeFilters?.cities,
    activeFilters?.standardTitles,
    activeFilters?.workModes,
    activeFilters?.experienceLevels,
  ]);

  const filteredCompanies = useMemo(() => {
    const query = companyQuery.trim().toLowerCase();
    return companyOptions.filter((item) => item.toLowerCase().includes(query));
  }, [companyOptions, companyQuery]);

  const filteredCities = useMemo(() => {
    const query = cityQuery.trim().toLowerCase();
    return cityOptions.filter((item) => item.toLowerCase().includes(query));
  }, [cityOptions, cityQuery]);

  const filteredStandardTitles = useMemo(() => {
    const query = standardTitleQuery.trim().toLowerCase();
    return standardTitleOptions.filter((item) => item.toLowerCase().includes(query));
  }, [standardTitleOptions, standardTitleQuery]);

  const pushSearch = (nextFilters, nextQueryText = searchText) => {
    onSearch({
      queryText: nextQueryText,
      filters: {
        companies: nextFilters.companies || [],
        cities: nextFilters.cities || [],
        standardTitles: nextFilters.standardTitles || [],
        workModes: nextFilters.workModes || [],
        experienceLevels: nextFilters.experienceLevels || [],
      },
    });
  };

  const runSearch = () => {
    pushSearch({
      companies: selectedCompanies,
      cities: selectedCities,
      standardTitles: selectedStandardTitles,
      workModes: selectedWorkModes,
      experienceLevels: selectedExperienceLevels,
    });
    setActiveMenu('');
  };

  const clearActiveMenuSelection = () => {
    if (activeMenu === 'company') {
      setSelectedCompanies([]);
      setCompanyQuery('');
      pushSearch({
        companies: [],
        cities: selectedCities,
        standardTitles: selectedStandardTitles,
        workModes: selectedWorkModes,
        experienceLevels: selectedExperienceLevels,
      });
      return;
    }

    if (activeMenu === 'city') {
      setSelectedCities([]);
      setCityQuery('');
      pushSearch({
        companies: selectedCompanies,
        cities: [],
        standardTitles: selectedStandardTitles,
        workModes: selectedWorkModes,
        experienceLevels: selectedExperienceLevels,
      });
      return;
    }

    if (activeMenu === 'standardtitle') {
      setSelectedStandardTitles([]);
      setStandardTitleQuery('');
      pushSearch({
        companies: selectedCompanies,
        cities: selectedCities,
        standardTitles: [],
        workModes: selectedWorkModes,
        experienceLevels: selectedExperienceLevels,
      });
      return;
    }

    if (activeMenu === 'workmode') {
      setSelectedWorkModes([]);
      pushSearch({
        companies: selectedCompanies,
        cities: selectedCities,
        standardTitles: selectedStandardTitles,
        workModes: [],
        experienceLevels: selectedExperienceLevels,
      });
      return;
    }

    if (activeMenu === 'experience') {
      setSelectedExperienceLevels([]);
      pushSearch({
        companies: selectedCompanies,
        cities: selectedCities,
        standardTitles: selectedStandardTitles,
        workModes: selectedWorkModes,
        experienceLevels: [],
      });
    }
  };

  const clearAll = () => {
    const clearedFilters = {
      companies: [],
      cities: [],
      standardTitles: [],
      workModes: [],
      experienceLevels: [],
    };
    setSelectedCompanies([]);
    setSelectedCities([]);
    setSelectedStandardTitles([]);
    setSelectedWorkModes([]);
    setSelectedExperienceLevels([]);
    setCompanyQuery('');
    setCityQuery('');
    setStandardTitleQuery('');
    onSearchTextChange('');
    pushSearch(clearedFilters, '');
    setActiveMenu('');
  };

  const handleGoHome = () => {
    setSelectedCompanies([]);
    setSelectedCities([]);
    setSelectedStandardTitles([]);
    setSelectedWorkModes([]);
    setSelectedExperienceLevels([]);
    setCompanyQuery('');
    setCityQuery('');
    setStandardTitleQuery('');
    setActiveMenu('');
    if (typeof onGoHome === 'function') onGoHome();
  };

  const getMenuTitle = () => {
    if (activeMenu === 'company') return 'Companies';
    if (activeMenu === 'city') return 'Cities';
    if (activeMenu === 'standardtitle') return 'Standard Titles';
    if (activeMenu === 'workmode') return 'Work Mode';
    if (activeMenu === 'experience') return 'Experience';
    return 'Filters';
  };

  const removeSelectedFilter = (kind, value) => {
    const nextCompanies =
      kind === 'company' ? removeValue(selectedCompanies, value) : selectedCompanies;
    const nextCities = kind === 'city' ? removeValue(selectedCities, value) : selectedCities;
    const nextStandardTitles =
      kind === 'standardtitle'
        ? removeValue(selectedStandardTitles, value)
        : selectedStandardTitles;
    const nextWorkModes =
      kind === 'workmode' ? removeValue(selectedWorkModes, value) : selectedWorkModes;
    const nextExperienceLevels =
      kind === 'experience'
        ? removeValue(selectedExperienceLevels, value)
        : selectedExperienceLevels;

    setSelectedCompanies(nextCompanies);
    setSelectedCities(nextCities);
    setSelectedStandardTitles(nextStandardTitles);
    setSelectedWorkModes(nextWorkModes);
    setSelectedExperienceLevels(nextExperienceLevels);

    pushSearch({
      companies: nextCompanies,
      cities: nextCities,
      standardTitles: nextStandardTitles,
      workModes: nextWorkModes,
      experienceLevels: nextExperienceLevels,
    });
  };

  const renderSelectedPill = (kind, rawValue, label = rawValue) => (
    <button
      key={`sel-${kind}-${rawValue}`}
      type="button"
      className="filter-selected-pill"
      onClick={() => removeSelectedFilter(kind, rawValue)}
      aria-label={`Remove ${label} filter`}
      title={`Remove ${label}`}
    >
      <span className="filter-selected-pill-text">{label}</span>
      <span className="filter-selected-pill-remove" aria-hidden="true">
        ×
      </span>
    </button>
  );

  return (
    <div className="search-shell">
      <div className="search-input-wrapper">
        <input
          className="search-input"
          type="text"
          placeholder="Search for jobs..."
          value={searchText}
          onChange={(event) => onSearchTextChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') runSearch();
          }}
        />
        <div className="search-actions">
          <button className="search-btn" type="button" onClick={runSearch}>
            Search
          </button>
        </div>
      </div>

      <div className="filter-chip-shell">
        <div className="filter-chip-row">
          <button className="filter-chip filter-chip-home" type="button" onClick={handleGoHome}>
            <span className="filter-chip-home-icon" aria-hidden="true">
              <HomeIcon />
            </span>
            Home
          </button>
          <button
            className={`filter-chip ${activeMenu === 'company' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveMenu((prev) => (prev === 'company' ? '' : 'company'))}
          >
            Company
            {selectedCompanies.length ? (
              <span className="filter-chip-count">{selectedCompanies.length}</span>
            ) : null}
          </button>
          <button
            className={`filter-chip ${activeMenu === 'city' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveMenu((prev) => (prev === 'city' ? '' : 'city'))}
          >
            City
            {selectedCities.length ? <span className="filter-chip-count">{selectedCities.length}</span> : null}
          </button>
          <button
            className={`filter-chip ${activeMenu === 'standardtitle' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveMenu((prev) => (prev === 'standardtitle' ? '' : 'standardtitle'))}
          >
            Title
            {selectedStandardTitles.length ? (
              <span className="filter-chip-count">{selectedStandardTitles.length}</span>
            ) : null}
          </button>
          <button
            className={`filter-chip ${activeMenu === 'workmode' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveMenu((prev) => (prev === 'workmode' ? '' : 'workmode'))}
          >
            Remote
            {selectedWorkModes.length ? (
              <span className="filter-chip-count">{selectedWorkModes.length}</span>
            ) : null}
          </button>
          <button
            className={`filter-chip ${activeMenu === 'experience' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveMenu((prev) => (prev === 'experience' ? '' : 'experience'))}
          >
            Experience
            {selectedExperienceLevels.length ? (
              <span className="filter-chip-count">{selectedExperienceLevels.length}</span>
            ) : null}
          </button>
          <button className="clear-all-btn" type="button" onClick={clearAll}>
            Clear all
          </button>
        </div>

        {selectedCount ? (
          <div className="filter-selected-summary">
            {selectedCompanies.slice(0, 2).map((item) => renderSelectedPill('company', item))}
            {selectedCities.slice(0, 2).map((item) => renderSelectedPill('city', item))}
            {selectedStandardTitles
              .slice(0, 2)
              .map((item) => renderSelectedPill('standardtitle', item))}
            {selectedWorkModes
              .slice(0, 3)
              .map((item) => renderSelectedPill('workmode', item))}
            {selectedExperienceLevels.slice(0, 3).map((item) =>
              renderSelectedPill(
                'experience',
                item,
                EXPERIENCE_OPTIONS.find((option) => option.value === item)?.label || item
              )
            )}
          </div>
        ) : null}

        {activeMenu ? (
          <div className="chip-dropdown-panel">
            <div className="filter-chip-header">
              <div className="filter-chip-title">{getMenuTitle()}</div>
              <button className="filter-chip-clear" type="button" onClick={clearActiveMenuSelection}>
                Clear
              </button>
            </div>
            {activeMenu === 'company' ? (
              <>
                <input
                  className="filter-search-input"
                  type="text"
                  placeholder="Search company"
                  value={companyQuery}
                  onChange={(event) => setCompanyQuery(event.target.value)}
                />
                <div className="filter-options-scroll">
                  {filteredCompanies.map((item) => (
                    <label key={`company-${item}`} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedCompanies.includes(item)}
                        onChange={() => setSelectedCompanies((prev) => toggleValue(prev, item))}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                  {!filteredCompanies.length ? (
                    <div className="filter-chip-empty">No company match</div>
                  ) : null}
                </div>
              </>
            ) : null}

            {activeMenu === 'city' ? (
              <>
                <input
                  className="filter-search-input"
                  type="text"
                  placeholder="Search city"
                  value={cityQuery}
                  onChange={(event) => setCityQuery(event.target.value)}
                />
                <div className="filter-options-scroll">
                  {filteredCities.map((item) => (
                    <label key={`city-${item}`} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedCities.includes(item)}
                        onChange={() => setSelectedCities((prev) => toggleValue(prev, item))}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                  {!filteredCities.length ? <div className="filter-chip-empty">No city match</div> : null}
                </div>
              </>
            ) : null}

            {activeMenu === 'standardtitle' ? (
              <>
                <input
                  className="filter-search-input"
                  type="text"
                  placeholder="Search standard title"
                  value={standardTitleQuery}
                  onChange={(event) => setStandardTitleQuery(event.target.value)}
                />
                <div className="filter-options-scroll">
                  {filteredStandardTitles.map((item) => (
                    <label key={`standard-title-${item}`} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedStandardTitles.includes(item)}
                        onChange={() =>
                          setSelectedStandardTitles((prev) => toggleValue(prev, item))
                        }
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                  {!filteredStandardTitles.length ? (
                    <div className="filter-chip-empty">No standard title match</div>
                  ) : null}
                </div>
              </>
            ) : null}

            {activeMenu === 'workmode' ? (
              <div className="filter-options-scroll">
                {WORK_MODE_OPTIONS.map((item) => (
                  <label key={`work-mode-${item.value}`} className="filter-option">
                    <input
                      type="checkbox"
                      checked={selectedWorkModes.includes(item.value)}
                      onChange={() => setSelectedWorkModes((prev) => toggleValue(prev, item.value))}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            ) : null}

            {activeMenu === 'experience' ? (
              <div className="filter-options-scroll">
                {EXPERIENCE_OPTIONS.map((item) => (
                  <label key={`experience-${item.value}`} className="filter-option">
                    <input
                      type="checkbox"
                      checked={selectedExperienceLevels.includes(item.value)}
                      onChange={() =>
                        setSelectedExperienceLevels((prev) => toggleValue(prev, item.value))
                      }
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            ) : null}

            <div className="filter-chip-footer">
              <button className="filter-action-btn" type="button" onClick={() => setActiveMenu('')}>
                Close
              </button>
              <button className="filter-action-btn primary" type="button" onClick={runSearch}>
                Apply
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchPanel;
